import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { PW, pewterButton } from "./pewter-shell";

import { Icon } from "@majourneys/icons";
export interface Crumb {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
  className?: string;
}

const BACK_STYLE: CSSProperties = {
  ...pewterButton,
  color: PW.navy,
};

/**
 * The parent the back button leads to: the nearest crumb before the current
 * page that has somewhere to go.
 */
export function backCrumb(items: readonly Crumb[]): Crumb | null {
  for (let i = items.length - 2; i >= 0; i--) {
    if (items[i]?.href) return items[i]!;
  }
  return null;
}

/**
 * Lab #19: one back button to the list, then the trail as quiet text. Four
 * pills read as four filters; most people only want "back". The current page
 * is left out of the trail, since the h1 right below already names it.
 *
 * The crumbs are plain links built from the page's own path, so they carry no
 * query string today; the back button keeps that behaviour.
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  const { t } = useTranslation();
  const back = backCrumb(items);
  // The trail stops before the back button's crumb, so the parent is not
  // named twice in a row.
  const trail = items.slice(0, back ? items.indexOf(back) : -1);

  return (
    <nav
      aria-label={t("common.breadcrumb")}
      className={cn("hidden min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5 sm:flex", className)}
    >
      {back && (
        <a
          href={back.href}
          className="inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full px-3 font-sans text-[12.5px] font-semibold"
          style={BACK_STYLE}
          data-testid="breadcrumb-back"
        >
          <Icon name="ri-arrow-left-line" className="shrink-0 text-[14px] rtl:-scale-x-100" aria-hidden />
          {back.label}
        </a>
      )}
      {trail.length > 0 && (
        <ol className="flex min-w-0 flex-wrap items-center font-sans text-[12px]" style={{ color: PW.muted }}>
          {trail.map((item, i) => (
            <li key={`${item.label}-${i}`} className="inline-flex items-center">
              {i > 0 && (
                <Icon
                  name="ri-arrow-right-s-line"
                  className="mx-0.5 shrink-0 text-[11px] rtl:-scale-x-100"
                  aria-hidden
                />
              )}
              {item.href ? (
                <a
                  href={item.href}
                  className="inline-flex min-h-6 items-center hover:underline hover:underline-offset-2"
                  style={{ color: PW.muted }}
                >
                  {item.label}
                </a>
              ) : (
                <span className="inline-flex min-h-6 items-center">{item.label}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}
